import axios from "axios";
import { FiltersRequiredError, NotImplementedError, parseNewNewYorkApiResponse } from "../../../..";
import { ObservableHandler } from "../../../../handlers";
import { Observable } from "rxjs";
import { parseIdFilter } from "../../../../handlers/filter";
import { isServicePage } from "../../../../handlers/page";
import { toQueryParams } from "./filters/params";
import { pubCapPctToServicePubCap, servicePubCapToPubCapPct } from "../../../../models/publisher/utils";
import { serviceToPublisherOptimizationTemplate } from "../../../../models/publisher/optimizationTemplate";
/**
 * Thrown by `updatePubCap` when the target publisher is not part of the
 * template's current `pub_settings`. Lets callers distinguish this expected
 * failure mode from generic fetch/PATCH errors.
 */
export class PublisherCapNotInTemplateError extends Error {
    constructor(templateId, publisherId) {
        super(`Publisher ${publisherId} is not part of template ${templateId}`);
        this.name = "PublisherCapNotInTemplateError";
        this.templateId = templateId;
        this.publisherId = publisherId;
    }
}
/**
 * Converts service response to client format following FE patterns
 */
export function servicePublisherCapToPublisherCap(serviceCap) {
    return {
        publisherId: serviceCap.pub_id,
        pubCap: servicePubCapToPubCapPct(serviceCap.pub_cap_pct) ?? 0,
        publisherName: "" // Will be populated by frontend publisher lookup
    };
}
/**
 * Handles publisher cap operations.
 * Caps are associated with publishers and represent specific limits or thresholds.
 */
class PublisherCaps extends ObservableHandler {
    constructor(sdk) {
        super(sdk, "publishers.optimizationTemplates.publisherCaps");
    }
    /**
     * Find a cap based on a publisher ID.
     *
     * @param filters: filter publisher caps by a given publisher ID (e.g., {where: [field: "id", type: FilterTypes.EQ, value: "1"]}).
     *          Supports (and requires):
     *              {FilterTypes.EQ, "id"}
     */
    findItems(filters) {
        const idFilter = parseIdFilter(filters);
        if (!idFilter) {
            throw new FiltersRequiredError("id");
        }
        return new Observable((subscriber) => {
            this.getPublisherCaps(filters)
                .then((caps) => {
                subscriber.next(caps);
                subscriber.complete();
            })
                .catch((error) => {
                subscriber.error(error);
            });
        });
    }
    async getPublisherCaps(filters = {}) {
        const templateId = filters.where?.find((filter) => filter.field === "id")
            ?.value;
        if (!templateId) {
            throw new FiltersRequiredError("templateId");
        }
        const params = toQueryParams(filters);
        const pageSize = filters?.paging?.size || 10;
        const url = `${this.sdk.urls.baseAPIUrl}/publisher-template-caps/${templateId}${params}`;
        return this.cache.promise(`${url}`, () => axios
            .get(`${url}`, {
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then((res) => {
            const rawCapData = parseNewNewYorkApiResponse(res);
            // Convert service response to client format
            const convertedData = rawCapData.data.map(servicePublisherCapToPublisherCap);
            let results = convertedData;
            if (isServicePage(rawCapData)) {
                results = {
                    page: {
                        count: rawCapData.paging_info.count,
                        token: rawCapData.paging_info.token,
                        size: pageSize
                    },
                    data: convertedData
                };
            }
            return results;
        })
            .catch((error) => {
            throw error;
        }));
    }
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    make(publisherCap) {
        throw new NotImplementedError("make");
    }
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    saveItem(publisherCap) {
        throw new NotImplementedError("save");
    }
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    async deleteItem(id) {
        throw new NotImplementedError("delete");
    }
    /**
     * Append publishers to an Optimization Template at a default cap.
     * Fetches current PubSettings, unions with new publisher IDs at defaultPubCap,
     * and PATCHes the merged list. Silently skips publisher IDs already present.
     *
     * @param templateId - The template to modify.
     * @param publisherIds - Publisher IDs to add.
     * @param opts.defaultPubCap - Cap in server units (0-10000, where 10000 = 100.00%).
     *   Defaults to 10000.
     */
    async addPublishers(templateId, publisherIds, opts) {
        if (!publisherIds.length) {
            throw new FiltersRequiredError("publisherIds");
        }
        const defaultPubCap = opts?.defaultPubCap ?? 10000;
        return this.patchPubSettings(templateId, (current) => {
            // catch duplicate IDs within `publisherIds`
            const seen = new Set(current.map((s) => s.pub_id));
            const additions = publisherIds
                .filter((id) => {
                if (seen.has(id)) {
                    return false;
                }
                seen.add(id);
                return true;
            })
                .map((id) => ({
                pub_id: id,
                pub_cap: { pub_cap_pct: defaultPubCap },
                bundle_rules: [],
                channel_rules: []
            }));
            return [...current, ...additions];
        });
    }
    /**
     * Builds a `patchPubSettings` mutator that applies one or more cap updates.
     * Shared by `updatePubCap` (single-entry) and `updatePubCaps` (bulk).
     *
     * `updates` values are in DISPLAY units (0-100). Conversion to server units
     * (0-10000) happens inside via `pubCapPctToServicePubCap` so callers never
     * have to think about wire format.
     *
     * Throws `PublisherCapNotInTemplateError` on the FIRST publisherId that
     * isn't currently in the template — so validation fails before we build
     * the PATCH body and before any partial state hits the wire.
     */
    buildCapUpdateMutator(templateId, updates) {
        return (current) => {
            const currentIds = new Set(current.map((s) => s.pub_id));
            for (const publisherId of Object.keys(updates)) {
                if (!currentIds.has(publisherId)) {
                    throw new PublisherCapNotInTemplateError(templateId, publisherId);
                }
            }
            return current.map((s) => {
                if (!(s.pub_id in updates))
                    return s;
                const servicePubCap = pubCapPctToServicePubCap(updates[s.pub_id]);
                return { ...s, pub_cap: { pub_cap_pct: servicePubCap } };
            });
        };
    }
    /**
     * Update a single publisher's cap on an Optimization Template.
     * Throws if publisherId is not currently in the template.
     *
     * @param pubCap - New cap in DISPLAY units (0-100). Converted to server
     *                units internally.
     */
    async updatePubCap(templateId, publisherId, pubCap) {
        return this.patchPubSettings(templateId, this.buildCapUpdateMutator(templateId, { [publisherId]: pubCap }));
    }
    /**
     * Bulk-update publisher caps on an Optimization Template in a single PATCH.
     * Preferred over N calls to `updatePubCap` when applying more than one edit —
     * saves N-1 round trips (each single-pub call does its own GET + PATCH).
     * Throws if any provided publisherId is not currently in the template.
     *
     * @param updates - Map of publisherId → new cap in DISPLAY units (0-100).
     *                  Converted to server units internally.
     */
    async updatePubCaps(templateId, updates) {
        if (!Object.keys(updates).length) {
            throw new FiltersRequiredError("updates");
        }
        return this.patchPubSettings(templateId, this.buildCapUpdateMutator(templateId, updates));
    }
    /**
     * Remove one or more publishers from an Optimization Template.
     * Silently skips publisher IDs not currently in the template.
     */
    async removePublishers(templateId, publisherIds) {
        if (!publisherIds.length) {
            throw new FiltersRequiredError("publisherIds");
        }
        const toRemove = new Set(publisherIds);
        return this.patchPubSettings(templateId, (current) => current.filter((s) => !toRemove.has(s.pub_id)));
    }
    // Fetches the full template, applies the mutator to its pub_settings, and
    // PATCHes the merged list. The BE's delete-what-isn't-in-payload behavior
    // becomes a no-op because we always send the union of existing + delta.
    async patchPubSettings(templateId, mutator) {
        const getUrl = `${this.sdk.urls.baseAPIUrl}/publisher-template/${templateId}`;
        const getRes = await axios.get(getUrl, { headers: { "Content-Type": "application/json" } });
        const current = parseNewNewYorkApiResponse(getRes).data.pub_settings ??
            [];
        const nextPubSettings = mutator(current);
        const patchUrl = `${this.sdk.urls.baseAPIUrl}/publisher-templates`;
        const patchRes = await axios.patch(patchUrl, { data: [{ id: templateId, pub_settings: nextPubSettings }] }, { headers: { "Content-Type": "application/json" } });
        const updated = parseNewNewYorkApiResponse(patchRes).data[0];
        this.cache.clear();
        return serviceToPublisherOptimizationTemplate(updated);
    }
}
export default PublisherCaps;
//# sourceMappingURL=index.js.map